import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { useDataContext } from "@/app/_context";

export type PlayAtTarget = {
  /** Index in the current playlist. */
  index: number;
  /** Time on that session's stream, in milliseconds. */
  playerMs: number;
};

/**
 * Plays a session from a given time, switching sessions first when needed.
 * The player can only seek once the stream has loaded — a seek before that is
 * dropped — and the first visit to the second session can play the author's
 * video bio before it (see VmPlayer), which hides the column's tabs meanwhile.
 * So a seek that can't happen yet waits here, in the column, rather than in
 * the tab that asked for it.
 */
export function usePlayAt() {
  const {
    currentSessionIndex,
    setCurrentSessionIndex,
    isBusinessCardPlaying,
    setSkipToTime,
    setIsSeeking,
    playerInstance,
  } = useDataContext();
  const pending = useRef<PlayAtTarget | null>(null);
  const state = useRef({
    currentSessionIndex,
    isBusinessCardPlaying,
    playerInstance,
  });

  useLayoutEffect(() => {
    state.current = {
      currentSessionIndex,
      isBusinessCardPlaying,
      playerInstance,
    };
  });

  const seek = useCallback(
    (playerMs: number) => {
      setIsSeeking(true);
      // 0 reads as "no seek" in the player, as in HighlightBrick.
      setSkipToTime(playerMs || 0.0001);
    },
    [setIsSeeking, setSkipToTime]
  );

  useEffect(() => {
    if (!playerInstance) return;
    const onCanPlay = () => {
      const target = pending.current;
      const { currentSessionIndex: index, isBusinessCardPlaying: bio } =
        state.current;
      if (!target || bio || target.index !== index) return;
      pending.current = null;
      seek(target.playerMs);
    };
    playerInstance.addEventListener("can-play", onCanPlay);
    return () => playerInstance.removeEventListener("can-play", onCanPlay);
  }, [playerInstance, seek]);

  // The viewer went somewhere else before the stream loaded: forget the seek.
  useEffect(() => {
    const target = pending.current;
    if (
      target &&
      target.index !== currentSessionIndex &&
      !isBusinessCardPlaying
    ) {
      pending.current = null;
    }
  }, [currentSessionIndex, isBusinessCardPlaying]);

  return useCallback(
    (target: PlayAtTarget) => {
      const {
        currentSessionIndex: index,
        isBusinessCardPlaying: bio,
        playerInstance: player,
      } = state.current;
      if (target.index === index && !bio && player?.state.canPlay) {
        return seek(target.playerMs);
      }
      pending.current = target;
      if (target.index !== index) setCurrentSessionIndex(target.index);
    },
    [seek, setCurrentSessionIndex]
  );
}
